import { FormEvent, useEffect, useState } from "react";
import { Edit, Plus, RefreshCw, Save, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, PaginationMeta, getCurrentAdmin, logout } from "./api";
import {
  FaqCategoryOption,
  FaqItem,
  FaqItemPayload,
  deleteAdminFaqItem,
  listAdminFaqCategories,
  listAdminFaqItems,
  saveAdminFaqItem,
} from "@/services/faq";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

type FormState = {
  FaqCategoryId: string;
  Question: string;
  Answer: string;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  FaqCategoryId: "",
  Question: "",
  Answer: "",
  IsActive: true,
  SortOrder: "100",
};

function itemToForm(item: FaqItem): FormState {
  return {
    FaqCategoryId: item.faqCategoryId ? String(item.faqCategoryId) : "",
    Question: item.question,
    Answer: item.answer,
    IsActive: item.isActive,
    SortOrder: String(item.sortOrder || 100),
  };
}

export default function AdminFaqItems() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<FaqItem[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<FaqCategoryOption[]>([]);
  const [counts, setCounts] = useState({ Active: 0, Inactive: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<FaqItem | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(filters = { Status: status, FaqCategoryId: categoryId, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminFaqItems(filters);
      setItems(data.Items);
      setCounts(data.Counts || { Active: 0, Inactive: 0 });
      setCategoryOptions(data.Categories || []);
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "FAQ 問答資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", FaqCategoryId: "", Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function ensureCategoryOptions() {
    if (categoryOptions.length) return categoryOptions;
    try {
      const categories = await listAdminFaqCategories();
      const options = categories.filter((category) => category.isActive).map((category) => ({ id: category.id, name: category.name }));
      setCategoryOptions(options);
      return options;
    } catch {
      return categoryOptions;
    }
  }

  async function openCreateForm() {
    await ensureCategoryOptions();
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  async function openEditForm(item: FaqItem) {
    await ensureCategoryOptions();
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.FaqCategoryId) {
      setFormMessage("請選擇 FAQ 類別。");
      return;
    }
    if (!form.Question.trim()) {
      setFormMessage("請輸入問題。");
      return;
    }
    if (!form.Answer.trim()) {
      setFormMessage("請輸入答案。");
      return;
    }

    const payload: FaqItemPayload = {
      FaqCategoryId: Number(form.FaqCategoryId),
      Question: form.Question.trim(),
      Answer: form.Answer.trim(),
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminFaqItem(editingItem?.id || null, payload);
      closeForm();
      await load({ Status: status, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "FAQ 問答儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: FaqItem) {
    const confirmed = await confirm({
      title: "刪除 FAQ 問答？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.question}</strong> 嗎？
          <br />
          刪除後前台也會立即移除，且無法復原。
        </>
      ),
      confirmText: "刪除問答",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminFaqItem(item.id);
      await load({ Status: status, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "FAQ 問答刪除失敗。");
    }
  }

  const allItemCount = counts.Active + counts.Inactive || pagination.Total;

  return (
    <AdminLayout sectionLabel="內容管理" title="FAQ 問答管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { value: "", label: "全部問答", count: allItemCount, tone: "text-[#315746]" },
            { value: "Active", label: "啟用中", count: counts.Active, tone: "text-[#245438]" },
            { value: "Inactive", label: "已停用", count: counts.Inactive, tone: "text-[#59665f]" },
          ].map((option) => (
            <button
              className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === option.value ? "border-[#315746] bg-[#315746] text-white" : `border-[#dfe6e1] bg-white ${option.tone}`}`}
              key={option.value}
              onClick={() => {
                setStatus(option.value);
                load({ Status: option.value, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
              }}
              type="button"
            >
              <span className="text-xs font-bold">{option.label}</span>
              <span className="ml-3 text-lg font-bold">{option.count}</span>
            </button>
          ))}
        </div>

        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-3">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "", FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部問答</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allItemCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Active", FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">啟用中</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Active}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => load({ Status: "Inactive", FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage })} type="button">
            <p className="text-xs font-bold text-[#7b877f]">已停用</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Inactive}</p>
          </button>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="faq-item-status">狀態</label>
          <select id="faq-item-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">啟用</option>
            <option value="Inactive">停用</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[160px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="faq-item-category">分類</label>
          <select id="faq-item-category" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">全部分類</option>
            {categoryOptions.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="faq-item-keyword">關鍵字</label>
          <input id="faq-item-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="問題或答案內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => void openCreateForm()} type="button">
          <Plus size={20} />
          新增問答
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[960px] table-fixed border-separate border-spacing-0 text-left">
            <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[420px] px-4 py-3">問答</th>
                <th className="w-[130px] px-4 py-3">分類</th>
                <th className="w-[100px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[150px] px-4 py-3">更新時間</th>
                <th className="sticky right-0 z-50 w-[160px] bg-[#f0f3f1] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={6}>沒有符合條件的 FAQ 問答。</td></tr>
              ) : items.map((item) => {
                const categoryName = categoryOptions.find((option) => option.id === item.faqCategoryId)?.name || "-";
                return (
                  <tr className="border-t border-[#edf0ee]" key={item.id}>
                    <td className="px-4 py-4">
                      <strong className="block text-[#203d32]">{item.question}</strong>
                      <div className="mt-1 line-clamp-2 text-sm text-[#59665f]">{item.answer}</div>
                    </td>
                    <td className="px-4 py-4 text-sm font-bold text-[#315746]">{categoryName}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.isActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.isActive ? "啟用" : "停用"}</span>
                    </td>
                    <td className="px-4 py-4">{item.sortOrder}</td>
                    <td className="px-4 py-4 text-sm text-[#59665f]">{item.updatedAt || "-"}</td>
                    <td className="sticky right-0 z-10 bg-white px-4 py-4 shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                      <div className="grid gap-2">
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => void openEditForm(item)} type="button">
                          <Edit size={16} />
                          編輯
                        </button>
                        <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                          <Trash2 size={16} />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, FaqCategoryId: categoryId, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[640px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯 FAQ 問答" : "新增 FAQ 問答"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">問答會顯示於前台 /faq 頁面對應分類底下。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>所屬類別 <span className="text-[#c3423f]">*</span></span>
                <select
                  className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                  value={form.FaqCategoryId}
                  onChange={(event) => setForm((current) => ({ ...current, FaqCategoryId: event.target.value }))}
                >
                  <option value="">請選擇分類</option>
                  {categoryOptions.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>問題 <span className="text-[#c3423f]">*</span></span>
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Question} onChange={(event) => setForm((current) => ({ ...current, Question: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>答案 <span className="text-[#c3423f]">*</span></span>
                <textarea className="min-h-[140px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Answer} onChange={(event) => setForm((current) => ({ ...current, Answer: event.target.value }))} />
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                排序
                <input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsActive} description="停用後不會顯示於前台 FAQ 頁面。" label="啟用問答" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存問答"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
